import { getOptimizelyAttributes } from '#core/utils/p13n'
import { OptimizelyDecideOption } from '@optimizely/optimizely-sdk'

export const useOptimizely = () => {
  const {
    configP13n: { enableOptimizely },
    configOptimizelyWebExperiments: { enabled }
  } = useFeatureFlags()
  const auth = useAuthStore()
  const cart = useCartStore()
  const userStore = useUserStore()
  const route = useRoute()
  const region = useRegion()
  const { width, height } = useWindowSize()
  const optaNonConsentCookie = useCookie('OptanonConsent')
  const userId = useCookie('optimizelyUserId')
  const locationData = {
    country: useCookie('Edgescape-Country'),
    state: useCookie('Edgescape-State'),
    city: useCookie('Edgescape-City'),
    zip: useCookie('Edgescape-Zip'),
    latLong: useCookie('Edgescape-Lat-Long')
  }
  const visitNumber = useCookie('optimizelyVisitNumber')
  const { $optimizelyClient } = useNuxtApp()
  const optimizelyLandingPage = useCookie('optimizelyLandingPage')

  const defineUser = () => {
    const attributes = getOptimizelyAttributes(
      {
        route,
        cart,
        auth,
        userStore,
        windowSize: { width: width.value, height: height.value },
        visitNumber: visitNumber.value,
        landingPage: optimizelyLandingPage.value,
        locationData
      }
    )
    if (window) {
      window.optimizely = window.optimizely || []
      window.optimizely.push({
        type: 'user',
        attributes
      })
    }
    console.info('Optimizely attributes:', attributes)
    return $optimizelyClient?.createUserContext(userId.value as string, attributes)
  }

  const getTargetingConsent = (groups) => {
    const consent = checkConsentGroup(region === 'EMEA', route.query.preview === 'true', decodeURIComponent(optaNonConsentCookie.value || ''))
    return groups.every((key) => consent[key])
  }

  const decide = (key: string) => {
    if (!enableOptimizely) return
    const user = getTargetingConsent(['targeting']) ? defineUser() : null
    return user?.decide(key, [
      OptimizelyDecideOption.ENABLED_FLAGS_ONLY,
      OptimizelyDecideOption.IGNORE_USER_PROFILE_SERVICE,
    ])
  }

  const trackEvent = (eventKey: string, properties, revenue?) => {
    if (!enableOptimizely) return
    const user = getTargetingConsent(['targeting']) ? defineUser() : null
    const tags = {
      $opt_event_properties: properties,
      ...(revenue && { revenue })
    }
    if (window) {
      window.optimizely = window.optimizely || []
      window.optimizely.push({
        type: 'event',
        eventName: eventKey,
        tags: {
          ...(revenue && { revenue })
        },
        properties
      })
    }
    console.info(`Tracking ${eventKey} Event for Optimizely: tags were`, tags)
    user?.trackEvent(eventKey, tags)
  }

  const activateOptimizely = () => {
    window.optimizely = window.optimizely || []
    window.optimizely.push({
      type: 'activate'
    })
    if (userStore.profile) {
      window.dispatchEvent(new Event('vfUserDataAvailable'))
    }
    optimizelyLandingPage.value = ''
  }

  const initOptimizely = () => {
    if (!enabled || !isClient()) return
    watch(
      [() => route.path, () => userStore.profile],
      () => {
        activateOptimizely()
      }
    )
  }

  return {
    decide,
    trackEvent,
    initOptimizely,
    activateOptimizely
  }
}
